import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { moodEmoji, fmtDate, toUtc } from "@/lib/mood";
import { PageHeader, SectionTitle } from "@/components/kit";

const toB64 = (blob) => new Promise((res) => { const r = new FileReader(); r.onloadend = () => res(String(r.result).split(",")[1] || ""); r.readAsDataURL(blob); });
const MIN_SECS = 2;

export default function Voice() {
  const [rec, setRec] = useState(false);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const startedAt = useRef(0);
  const [result, setResult] = useState(null);
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState([]);
  const mr = useRef(null);
  const chunks = useRef([]);
  const tick = useRef(null);

  const loadHistory = () => api.get("/voice/history?limit=10").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const m = new MediaRecorder(stream);
      chunks.current = [];
      m.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
      m.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: m.mimeType || chunks.current[0]?.type || "audio/webm" });
        if (Date.now() - startedAt.current < MIN_SECS * 1000 || blob.size < 1000) {
          toast.error("That was too short — hold on and talk for a few seconds");
          return;
        }
        analyze(blob);
      };
      startedAt.current = Date.now();
      m.start(1000);
      mr.current = m;
      setRec(true); setSecs(0); setResult(null);
      tick.current = setInterval(() => setSecs((s) => s + 1), 1000);
    } catch { toast.error("Microphone access is needed for voice check-ins"); }
  };
  const stop = () => { mr.current?.stop(); setRec(false); clearInterval(tick.current); };

  const analyze = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio_base64", await toB64(blob));
      fd.append("audio_format", blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm");
      const { data } = await api.post("/voice/analyze", fd);
      if (!data.success) toast.error(data.error); else setResult(data);
      loadHistory();
    } catch (e) {
      if (!e.response) toast.error("Couldn't upload your recording — check your connection and try again");
      else toast.error(errMsg(e, `Voice analysis failed (error ${e.response.status}). Please try again.`));
    }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this voice check-in?")) return;
    try { await api.delete(`/voice/${id}`); setHistory((h) => h.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch (e) { toast.error(errMsg(e, "Could not delete")); }
  };

  const save = async () => {
    try { await api.post(`/voice/create-checkin?voice_checkin_id=${result.voice_checkin_id}${notes ? `&additional_notes=${encodeURIComponent(notes)}` : ""}`); toast.success("Saved as a check-in 🎉"); setResult(null); setNotes(""); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const a = result?.analysis || {};
  return (
    <div className="max-w-3xl" data-testid="voice-page">
      <PageHeader back title="Voice Check-In" subtitle="Just talk about your day — AI picks up your mood and themes" />
      <div className="mm-card p-10 flex flex-col items-center text-center">
        <button onClick={rec ? stop : start} disabled={busy} data-testid="voice-record-button"
          className={`w-28 h-28 rounded-full flex items-center justify-center text-white transition-transform hover:scale-105 ${rec ? "mm-pulse-ring" : ""}`}
          style={{ background: rec ? "var(--mm-error)" : "var(--mm-primary)" }}>
          {busy ? <Loader2 size={40} className="animate-spin" /> : rec ? <Square size={36} /> : <Mic size={44} />}
        </button>
        <div className="font-display text-2xl font-bold mt-5" data-testid="voice-timer">{rec ? `0:${String(secs).padStart(2, "0")}` : busy ? "Analyzing…" : "Tap to record"}</div>
        <p className="text-sm text-mm-muted mt-1">Try: “Today I felt… because…”. 15–60 seconds works best.</p>
      </div>

      {result && (
        <div className="mm-card p-6 mt-6 mm-fade-up" data-testid="voice-result">
          <div className="flex items-center gap-4">
            <span className="text-5xl">{moodEmoji(a.mood_score || 5)}</span>
            <div><div className="font-display text-2xl font-bold" data-testid="voice-mood-score">Mood {a.mood_score}/10</div><div className="text-sm text-mm-muted capitalize">{a.overall_sentiment || a.sentiment}</div></div>
          </div>
          <p className="mm-soft p-4 mt-4 text-sm italic" data-testid="voice-transcription">“{result.transcription}”</p>
          {a.emotions?.length > 0 && <div className="flex flex-wrap gap-2 mt-4">{a.emotions.map((e) => <span key={e} className="mm-chip !cursor-default">{e}</span>)}</div>}
          {a.key_themes?.length > 0 && <p className="text-sm mt-3"><b>Themes:</b> {a.key_themes.join(", ")}</p>}
          {a.summary && <p className="text-sm mt-3">{a.summary}</p>}
          {a.suggestions?.length > 0 && <ul className="text-sm mt-3 list-disc pl-5 space-y-1">{a.suggestions.map((s) => <li key={s}>{s}</li>)}</ul>}
          <input className="mm-input mt-4" placeholder="Add a note (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="voice-notes-input" />
          <button className="mm-btn mt-4" onClick={save} data-testid="voice-save-checkin">Save as check-in</button>
        </div>
      )}

      <SectionTitle>Recent voice check-ins</SectionTitle>
      <div className="space-y-3">
        {history.map((h) => (
          <div key={h.id} className="mm-card p-4 flex gap-3" data-testid={`voice-history-${h.id}`}>
            <span className="text-2xl">{moodEmoji(h.mood_score || 5)}</span>
            <div className="flex-1 min-w-0"><div className="text-xs text-mm-muted">{fmtDate(toUtc(h.timestamp))} · Mood {h.mood_score}/10</div><p className="text-sm line-clamp-2">{h.transcription}</p></div>
            <button onClick={() => remove(h.id)} className="self-start p-1.5 rounded-full text-mm-muted hover:text-mm-error hover:bg-mm-error/10 transition-colors" aria-label="Delete voice check-in" data-testid={`voice-history-delete-${h.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {!history.length && <p className="text-sm text-mm-muted">No voice check-ins yet.</p>}
      </div>
    </div>
  );
}
